import React, { useState, useEffect } from 'react';
import {
  useElements,
  useStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  Button,
} from '@mui/material';
import apiClient from '../../lib/api';

const elementStyle = {
  base: {
    fontSize: '16px',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    color: '#333',
    '::placeholder': { color: '#aaa' },
  },
  invalid: {
    color: '#f44336',
  },
};

function StripeSplitFields({ card, cancel, updated }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [focused, setFocused] = useState(null);
  const [status, setStatus] = useState(''); // Status message
  const [clientSecret, setClientSecret] = useState(null);

  const handleFocus = (field) => setFocused(field);
  const handleBlur = () => setFocused(null);
  const handleError = (event) => {
    if (event.error) setError(event.error.message);
    else setError(null);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('Saving...');
  
    try {
      const data = await apiClient.createSetupIntent();
      setClientSecret(data.clientSecret);
    
      const cardNumberElement = elements?.getElement(CardNumberElement);
    
      const result = await stripe?.confirmCardSetup(data.clientSecret, {
        payment_method: {
          card: cardNumberElement, // only need to pass CardNumberElement
        },
      });
    
      if (result?.error) {
        setStatus(`Error: ${result.error.message}`);
      } else {
        await apiClient.setDefaultPaymentMethod();
        setStatus('Card saved!');
        updated();
        cancel();
      }
    } catch (error) {
      console.error('Error saving card:', error);
      setStatus('Error saving card. Please try again.');
    }
  };

  const renderStripeField = (id, label, Element) => (
    <FormControl fullWidth variant="outlined" margin="normal">
      <InputLabel shrink htmlFor={id}>{label}</InputLabel>
      <Box
        id={id}
        sx={{
          border: '1px solid',
          borderColor: error && focused === id ? 'error.main' : focused === id ? 'primary.main' : 'rgba(0, 0, 0, 0.23)',
          borderRadius: 1,
          padding: '18.5px 14px',
          marginTop: '8px',
          '&:hover': {
            borderColor: 'black',
          },
          boxShadow: focused === id ? '0 0 0 2px rgba(25, 118, 210, 0.25)' : 'none',
        }}
      >
        <Element
          options={{ style: elementStyle }}
          onFocus={() => handleFocus(id)}
          onBlur={handleBlur}
          onChange={handleError}
        />
      </Box>
      {error && focused === id && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );

  return (
    <>
      {renderStripeField('card-number', 'Card Number', CardNumberElement)}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          {renderStripeField('card-expiry', 'Expiration Date', CardExpiryElement)}
        </Box>
        <Box sx={{ flex: 1 }}>
          {renderStripeField('card-cvc', 'CVC', CardCvcElement)}
        </Box>
      </Box>

      <Button
        variant="contained"
        color="primary"
        fullWidth
        sx={{ mt: 2 }}
        disabled={!stripe || error}
        onClick={handleSubmit}
      >
      Save Card
      </Button>
      <p>{status}</p>

      {card && (
        <Button
          variant="contained"
          color="error"
          fullWidth
          sx={{ mt: 2 }}
          onClick={cancel}
        >
          Cancel
        </Button>
      )}

    </>
  );
}

export default StripeSplitFields;
